import styles from './template.css';
import template from './template';
import {AoflElement, customElement, property} from '@aofl/element';
import {i18nMixin} from '@aofl/i18n-mixin';
import {validationMixin, isRequired} from '@aofl/form-validate';
import {mapStatePropertiesMixin} from '@aofl/map-state-properties-mixin';
import {storeInstance} from '@aofl/store/dist/legacy';
import translations from '../i18n';
import {
  permissionGroupKeys,
  permissionGroups,
  sdoNs,
  userTypes,
  exceptionCodes,
  permissions, featureFlags
} from '@modules/constants-enumerate';
import {emailLike, phoneLike} from '@modules/custom-form-validators';
import '@modules/sdo-user-info';
import '@modules/phone-input';
import {ThreeWayToggle} from '@modules/three-way-toggle';
import {hasOneOfPermissions} from '@modules/permissions';
import {AnalyticEvent, AnalyticsTrackerQueue} from '@epg/epg-components';
import {ApiService} from '@modules/api-service';
import {restrictedPermissions} from '@modules/permissions';
import {toastStatus} from '@modules/i18n-labels';
import {Toast} from '@modules/toast';


const removeGroupPermission = (acc, item) => {
  acc[item] = false;
  return acc;
};

/**
 * @summary EditForm
 * @extends {mapStatePropertiesMixin(validationMixin(i18nMixin(AoflElement)))}
 */
@customElement('admin-form')
class AdminForm extends mapStatePropertiesMixin(validationMixin(i18nMixin(AoflElement))) {
  /**
   * Creates an instance of EditForm.
   */
  constructor() {
    super();
    this.translations = translations;
    this.storeInstance = storeInstance;

    const state = storeInstance.getState();
    this.data = state[sdoNs.ADMIN_UINFO];
    this.ownProfile = (this.data.id === this.loggedInUserId);
    this.showProfile = (state[sdoNs.ADMIN_UINFO].type === userTypes.ADMIN);
    this.inputDisabled = (this.showProfile) ? '' : 'disabled';
    this.showContactInfo = (this.userId !== null);

    this.validators = {
      firstName: {isRequired},
      lastName: {temp: () => true},
      email: {
        emailLike,
        checkLoginFields: this.checkLoginFields.bind(this),
        conflicting: (value) => {
          return !value || !this.conflictingEmail || value !== this.conflictingEmail;
        }
      },
      phone: {
        checkLoginFields: this.checkLoginFields.bind(this),
        phoneLike
      },
      contact_email: {
        checkContactFields: this.checkContactFields.bind(this),
        emailLike: this.checkContactEmailField.bind(this),
      },
      contact_phone: {
        checkContactFields: this.checkContactFields.bind(this),
        phoneLike: this.checkContactPhoneField.bind(this),
      },
      external_id: {temp: () => true},
    };
  }

  checkLoginFields() {
    return isRequired(this.phone) ||
      isRequired(this.email) ||
      this.email !== this.conflictingEmail;
  }

  checkContactFields() {
    if (typeof this.contactPhone === 'object') {
      this.contactPhone = '';
    }
    return (this.showProfile) ? isRequired(this.contactPhone) || isRequired(this.contactEmail) : true;
  }

  checkContactEmailField() {
    return (this.showProfile) ? emailLike(this.contactEmail) : true;
  }

  checkContactPhoneField() {
    // Doing !this.contactPhone because value comes in as null from server, but if user deletes it comes in as empty string
    return (this.showProfile) ? !this.contactPhone || phoneLike(this.contactPhone) : true;
  }

  /**
   * @readonly
   */
  static get is() {
    return 'admin-form';
  }

  @property({type: Boolean})
  ['require-password'] = false;

  @property({type: String, mapState: `${sdoNs.ADMIN_UINFO}.external_id`, store: storeInstance})
  externalId = '';

  @property({type: String, mapState: `${sdoNs.ADMIN_UINFO}.first_name`, store: storeInstance})
  firstName = '';

  @property({type: String, mapState: `${sdoNs.ADMIN_UINFO}.last_name`, store: storeInstance})
  lastName = '';

  @property({type: String, mapState: `${sdoNs.ADMIN_UINFO}.email`, store: storeInstance})
  email = '';

  @property({type: Boolean, attribute: true})
  loginEmailUpdated = false;

  @property({type: String, mapState: `${sdoNs.ADMIN_UINFO}.profile.contact_email`, store: storeInstance})
  contactEmail = '';

  @property({type: String, mapState: `${sdoNs.ADMIN_UINFO}.phone`, store: storeInstance})
  phone = '';

  @property({type: String, mapState: `${sdoNs.ADMIN_UINFO}.profile.contact_phone`, store: storeInstance})
  contactPhone = '';

  @property({type: Boolean, attribute: true})
  saving = false;

  @property({type: Object, attribute: true})
  error = {};

  @property({type: Number, mapState: `${sdoNs.ADMIN_UINFO}.id`, store: storeInstance})
  userId = null;

  @property({type: String, mapState: `${sdoNs.ADMIN_UINFO}.learning_assistant_status`, store: storeInstance})
  learningAssistantStatus = 'disabled';

  @property({type: Array, mapState: `${sdoNs.ADMIN_UINFO}.$roles`, store: storeInstance})
  roles = [];

  @property({type: Array, mapState: `${sdoNs.ADMIN_UINFO}.pendingRoles`, store: storeInstance})
  pendingRoles = [];

  @property({type: Number, mapState: `${sdoNs.SESSION}.id`, store: storeInstance})
  loggedInUserId;

  @property({type: Boolean, mapState: `${sdoNs.ADMIN_UINFO}.is_root_admin`, store: storeInstance})
  isRootAdmin = false;

  @property({type: Boolean, mapState: `${sdoNs.SESSION}.$isSchoolAdmin`, store: storeInstance})
  isSchoolAdmin = false;

  @property({type: Boolean, attribute: true})
  ownProfile = false;

  conflictingEmail = '';

  @property({type: Boolean})
  disabled = false;

  @property({type: Boolean})
  hasPerm = false;

  @property({type: Boolean})
  learningAssistantEnabled = false;

  @property({type: Boolean})
  savedLearningAssistant = false;

  @property({type: Boolean})
  savingRoles = false;

  @property({type: Boolean})
  rolesSavedSuccessfully = false;

  @property({type: String})
  tooltipId = '';

  @property({type: Object})
  defaultPermissions = {
    [permissionGroupKeys.ADMIN_ACCOUNTS]: {
      toggleState: ThreeWayToggle.VALUES.NONE,
      permissions: permissionGroups[permissionGroupKeys.ADMIN_ACCOUNTS].reduce(removeGroupPermission, {})
    },
    [permissionGroupKeys.MANUAL_ROSTER]: {
      toggleState: ThreeWayToggle.VALUES.NONE,
      permissions: permissionGroups[permissionGroupKeys.MANUAL_ROSTER]
        .reduce(removeGroupPermission, {})
    },
    [permissionGroupKeys.ROSTER_UPLOAD]: {
      toggleState: ThreeWayToggle.VALUES.NONE,
      permissions: permissionGroups[permissionGroupKeys.ROSTER_UPLOAD]
        .reduce(removeGroupPermission, {})
    },
    [permissionGroupKeys.LICENSE]: {
      toggleState: ThreeWayToggle.VALUES.NONE,
      permissions: permissionGroups[permissionGroupKeys.LICENSE]
        .reduce(removeGroupPermission, {})
    }
  };

  @property({type: Boolean, attribute: true})
  showProfile = false;

  @property({type: String})
  inputDisabled = '';

  @property({type: Boolean, attribute: true})
  showContactInfo = false;

  firstNameUpdated(e) {
    this.firstName = e.target.value.trim();
    this.storeInstance.commit({namespace: sdoNs.ADMIN_UINFO, mutationId: 'setFirst_name', payload: this.firstName});
    this.form.firstName.validate();
  }

  lastNameUpdated(e) {
    this.lastName = e.target.value.trim();
    this.storeInstance.commit({namespace: sdoNs.ADMIN_UINFO, mutationId: 'setLast_name', payload: this.lastName});
    this.form.lastName.validate();
  }

  validateFirstName(e) {
    this.form.firstName.validate();
  }

  emailUpdated(e) {
    if (e.target.value.trim() !== this.email) {
      this.error = {};
    }

    this.email = e.target.value.trim();
    this.storeInstance.commit({namespace: sdoNs.ADMIN_UINFO, mutationId: 'setEmail', payload: this.email});
    this.form.email.validate();
  }

  phoneUpdated(e) {
    this.phone = e.target.value.trim();
    this.storeInstance.commit({namespace: sdoNs.ADMIN_UINFO, mutationId: 'setPhone', payload: this.phone});
    this.form.phone.validate();
  }

  externalIdUpdated(e) {
    if (e.target.value.trim() !== this.contactEmail) {
      this.error = {};
    }

    this.externalId = e.target.value.trim();
    this.storeInstance.commit({namespace: sdoNs.ADMIN_UINFO, mutationId: 'setExternalId', payload: this.externalId});
    this.form.external_id.validate();
  }

  contactEmailUpdated(e) {
    if (e.target.value.trim() !== this.contactEmail) {
      this.error = {};
    }

    this.contactEmail = e.target.value.trim();
    const profile = {...this.data.profile, contact_email: this.contactEmail};
    this.storeInstance.commit({namespace: sdoNs.ADMIN_UINFO, mutationId: 'setProfile', payload: profile});
    this.form.contact_email.validate();
  }

  contactPhoneUpdated(e) {
    this.contactPhone = e.target.value;
    this.contactPhone = typeof this.contactPhone === 'string' ? this.contactPhone.trim() : '';
    const profile = {...this.data.profile, contact_phone: this.contactPhone};
    this.storeInstance.commit({namespace: sdoNs.ADMIN_UINFO, mutationId: 'setProfile', payload: profile});
    this.form.contact_phone.validate();
  }

  validateEmailAndPhone(e) {
    this.form.email.validate();
    this.form.phone.validate();
  }

  validateContactEmailAndPhone() {
    this.form.contact_phone.validate();
    this.form.contact_email.validate();
  }

  connectedCallback() {
    super.connectedCallback();
    const state = storeInstance.getState();
    this.learningAssistantEnabled = state[sdoNs.SESSION]?.settings?.feature_flags?.[featureFlags.ALLOW_LEARNING_ASSISTANT] || false;
  }

  learningAssistantUpdated(e) {
    this.savedLearningAssistant = false;
    const event = AnalyticEvent.create('user.element_interact', {
      element_id: e.target.id,
      element_name: e.target.name,
      element_value: e.target.checked ? 'on' : 'off',
    });
    AnalyticsTrackerQueue.enqueueEvent(event);
    ApiService.groupSettingsConfigureLearningAssistant(
      e.target.checked,
    ).then(() => {
      this.savedLearningAssistant = true;
    });
  }

  setTooltipId(e, id) {
    this.tooltipId = id;
    e.preventDefault();
  }

  tempId = 0;

  addRole() {
    storeInstance.commit({
      namespace: sdoNs.ADMIN_UINFO,
      mutationId: 'createRoles',
      payload: {tempId: this.tempId++, permissions: this.defaultPermissions}
    });
  }

  async submit(e) {
    e.preventDefault();
    e.stopPropagation();
    this.saving = true;
    const form = e.target;
    const formData = new FormData(form);
    formData.set('phone', this.phone);
    formData.set('contact-phone', this.contactPhone);

    this.form.validate();
    await this.form.validateComplete;

    if (this.form.valid) {
      this.dispatchEvent(new CustomEvent('admin-form-submit', {
        detail: {formData},
        composed: true
      }));
    }
  }

  saveRoles(e) {
    e.preventDefault();
    this.savingRoles = true;
    const newRoles = [];
    const state = storeInstance.getState();
    const deletedRoleIds = state[sdoNs.ADMIN_UINFO].deletedRoleIds || [];
    this.pendingRoles.forEach((role) => {
      if (!role.id) {
        newRoles.push(role);
      }
    });
    if (newRoles.length === 0 && state[sdoNs.ADMIN_UINFO].roles.length === 0) {
      this.savingRoles = false;
      this.rolesSavedSuccessfully = false;
      return Toast.error(toastStatus.pleaseAddRole);
    }
    const processedNewRoles = this.processPermissions(newRoles);
    ApiService.userUpdateRoles(
      this.data?.profile?.id,
      processedNewRoles,
      deletedRoleIds,
    )
      .then(() => {
        this.rolesSavedSuccessfully = true;
      })
      .finally(() => {
        this.savingRoles = false;
      })
      .catch(() => {
        this.rolesSavedSuccessfully = false;
      });
  }

  processPermissions(newRoles) {
    const permissions = [];
    for (let i = 0; i < newRoles.length; i++) {
      if (newRoles[i].permissions.group) {
        const permission = {
          access_data: {},
        };
        const restricted = restrictedPermissions(
          newRoles[i].permissions.groupLevel,
          parseInt(newRoles[i].permissions.tiersLevels, 10) + 1
        );

        if (newRoles[i].permissions.sponsor) {
          permission.access_data.sponsor = true;
        } else if (newRoles[i].permissions.hideStudent) {
          permission.access_data.hide_student = true;
        } else {
          for (const key in newRoles[i].permissions) {
            if (typeof(newRoles[i].permissions[key]) !== 'undefined') {
              for (const permissionKey in newRoles[i].permissions[key].permissions) {
                if (typeof(newRoles[i].permissions[key].permissions[permissionKey]) !== 'undefined' && restricted.indexOf(permissionKey) === -1) {
                  permission.access_data[permissionKey] = newRoles[i].permissions[key].permissions[permissionKey];
                }
              }
            }
          }
        }

        permission.group_id = newRoles[i].permissions.group;
        permissions.push(permission);
      }
    }
    return permissions;
  }

  mapStateProperties() {
    const state = storeInstance.getState();
    if (!state[sdoNs.SESSION].loggedIn) return;
    this.data = state[sdoNs.ADMIN_UINFO];

    if (
      (this.data.is_third_party_connected && this.data.managed_by) ||
      this.data.is_root_admin
    ) {
      this.disabled = true;
      this.hasPerms = (hasOneOfPermissions([permissions.USER_EDIT]));
    }
  }

  shouldUpdate(changedProperties) {
    if (changedProperties.has('error')) {
      if (this.error?.code === exceptionCodes.INPUT_CONFLICTING_PRODUCT_CREDENTIALS) {
        this.conflictingEmail = this.email;
        this.form.email.conflicting.validate();
      }
    }

    return true;
  }

  setSuccess() {
    this.saving = false;
    Toast.success(toastStatus.saveSuccess);
    this.form.reset();
  }

  async setErrors(errors) {
    this.saving = false;

    if (!errors) {
      return;
    }

    if (
      errors.code === exceptionCodes.INPUT_CONFLICTING_PRODUCT_CREDENTIALS ||
      (errors.code === exceptionCodes.INPUT_GENERAL && typeof errors.payload[0]?.email?.duplicate !== 'undefined')
    ) {
      this.conflictingEmail = this.email;
    }

    this.form.validate();
    await this.form.validationComplete;
  }

  /**
   *
   * @return {Object}
   */
  render() {
    return super.render({
      default: {
        template,
        styles: [styles]
      }
    });
  }
}

export default AdminForm;
